import Playback from '@/features/recording/Playback';
import Loader from '@/shared/components/Loader';
import RecorderButton from '@/features/recording/RecorderButton';
import { PlayIcon, RotateCcw } from 'lucide-react';
import PauseIcon from '@/images/SVG/pauseIcon.svg';
import MicIcon from '@/images/SVG/playIcon.svg';
import doneIcon from '@/images/SVG/doneIcon.svg';
import RecordingDiscardDialog from '@/features/recording/RecordingDiscardDialog';
import { useTranslation } from 'react-i18next';
import { formatTime } from '@/features/recording/recordingSession';

const recorderControlsGap = 'gap-[16px] md:gap-2';

export default function RecorderControls({
  containerRef,
  playback,
  recordPluginInstance,
  active,
  duration,
  showPlayback,
  playbackAudioUrl,
  playBackState,
  onPlaybackStateChange,
  playbackMuted,
  isPaused,
  onStartStop,
  onPlaybackPlayPause,
  onPauseResume,
  isDisabled,
  processingUrl,
  onDone,
  finalising,
  isProcessing,
}) {
  const { t } = useTranslation();

  return (
    <div className="recorder-wrapper">
      <div className={`w-full wave-container ${playback ? 'hidden' : 'block'}`}>
        <div className="flex items-center">
          <div className="flex-1 border-r-2 border-red-500" ref={containerRef}></div>
          {recordPluginInstance && active && (
            <div className="ml-2 flex justify-end">
              <div className="flex flex-col justify-center">
                <span className="dot"></span>
              </div>
              <span className="pl-3 mr-2 min-[350px]:mr-5 min-[350px]:pl-3 min-[350px]:w-[90px]">{formatTime(duration)}</span>
            </div>
          )}
        </div>
      </div>

      <div className={`w-full ${showPlayback ? 'block' : 'hidden'}`}>
        {showPlayback && (
          <Playback playbackAudioUrl={playbackAudioUrl} playBackState={playBackState} onPlaybackStateChange={onPlaybackStateChange} muted={playbackMuted}/>
        )}
      </div>
      <div className={`flex flex-row w-full mt-6 ${isPaused ? `${recorderControlsGap} grid md:grid-cols-2 grid-cols-1` : `${recorderControlsGap} flex-col sm:flex-row justify-between`}`}>
        <div className={`flex justify-start ${recorderControlsGap} ${isPaused ? 'grid grid-cols-2' : ''}`}>
          <RecordingDiscardDialog
            onConfirm={onStartStop}
            trigger={(
              <RecorderButton variant="outline" className="flex justify-center items-center w-full">
                <div className={`flex justify-center items-center ${isPaused ? 'gap-1': 'gap-[6px]'} `}>
                  <RotateCcw size={15} strokeWidth={3} className='text-red-500' />
                  <span>{t("actions.restart")}</span>
                </div>
              </RecorderButton>
            )}
          />
          {isPaused && (
            <RecorderButton
              onClick={onPlaybackPlayPause}
              variant="outline"
              disabled={recordPluginInstance && !(recordPluginInstance.isPaused() || !active)}
              className={`flex justify-center items-center w-full ${(recordPluginInstance && !(recordPluginInstance.isPaused() || !active))} ? 'hidden w-full' : 'block w-full'`}
            >
              {playBackState === 'pause' ?
                <div className="flex gap-[4px] justify-center items-center">
                  <PlayIcon size={16} className='text-gray-500' />
                  <span>{t("actions.play")}</span>
                </div>
                :
                <div className='flex gap-[4px] justify-center items-center'>
                  <img src={PauseIcon} alt="pause icon" className='w-4 h-4 object-contain' />
                  <span>{t("actions.pause")}</span>
                </div>
              }
            </RecorderButton>
          )}
        </div>
        <div className={`flex ${recorderControlsGap} w-full items-center ${isPaused ? 'grid grid-cols-2' : 'flex-col justify-end sm:flex-row'}`}>
          <RecorderButton key="pause-recording-button" onClick={onPauseResume} disabled={isDisabled || processingUrl} variant="outline" className={`w-full ${!isPaused && 'sm:w-fit'} items-center justify-center`}>
            {
              isDisabled || processingUrl ?
                <Loader spninnerClassName="m-auto" variant="small" /> :
                (recordPluginInstance.isPaused() ?
                  <div className='flex gap-[6px] items-center justify-center'>
                    <img src={MicIcon} alt='Continue Icon' className='w-4 h-4 object-contain' />
                    <span>{t("actions.resume")}</span>
                  </div> :
                  <div className='flex gap-[6px] items-center justify-center'>
                    <img src={PauseIcon} alt="pause icon" className='w-4 h-4 object-contain' />
                    <span>{t("actions.pause")}</span>
                  </div>
                )
            }
          </RecorderButton>

          <RecorderButton key="finish-recording-button" onClick={onDone} recordButton
            className={`flex items-center justify-center w-full ${!isPaused && 'sm:w-fit'}`}
            variant="outline"
            disabled={finalising || isProcessing}
          >
            <div className='flex justify-center items-center gap-[6px]'>
              { finalising
                ? <Loader variant='small'/>
                : <img src={doneIcon} alt='' className='w-4 h-4 object-contain mb-[1px]' />
              }
              <span>{finalising ? `${t("actions.finalizing")}...` : t("actions.done")}</span>
            </div>
          </RecorderButton>
        </div>
      </div>
    </div>
  );
}
